
import React from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/context/LanguageContext';
import { CheckCircle, Users, Clock, Shield, Video, MapPin, Star, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import MasterSEOController from '@/components/seo/MasterSEOController';

const HowItWorks = () => {
  const { t, getLocalizedPath, language } = useLanguage();
  
  const steps = [
    {
      number: 1,
      title: t('findYourTherapist'),
      description: t('browseOurVerifiedTherapists'),
      icon: Users,
      features: [t('advancedFilters'), t('professionalProfiles'), t('realReviews')]
    },
    {
      number: 2,
      title: t('bookYourSession'),
      description: t('scheduleYourAppointment'),
      icon: Calendar,
      features: [t('instantBooking'), t('flexibleScheduling'), t('securePayments')]
    },
    {
      number: 3,
      title: t('attendYourSession'),
      description: t('connectWithYourTherapist'),
      icon: Video,
      features: [t('secureVideoCall'), t('inPersonOption'), t('professionalSupport')]
    },
    {
      number: 4,
      title: t('continuousSupport'),
      description: t('ongoingTherapeuticRelationship'),
      icon: Star,
      features: [t('followUpSessions'), t('progressTracking'), t('therapistFeedback')]
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: t('verifiedProfessionals'),
      description: t('allTherapistsCertified')
    },
    {
      icon: Clock,
      title: t('flexibleScheduling'),
      description: t('bookSessionsAnytime')
    },
    {
      icon: MapPin,
      title: t('globalAccess'),
      description: t('therapistsWorldwide')
    },
    {
      icon: Video,
      title: t('multipleFormats'),
      description: t('onlineAndInPerson')
    }
  ];

  return (
    <>
      {/* Master SEO Controller */}
      <MasterSEOController 
        pageType="static"
        currentLanguage={language}
        customMetaTags={{
          title: "How It Works - Simple Steps to Start Therapy",
          description: "Learn how to find and connect with licensed therapists in 4 simple steps. Book online or in-person therapy sessions securely. Flexible scheduling, verified professionals, worldwide access.",
          keywords: ['how therapy works', 'online therapy process', 'book therapy session', 'psychotherapy steps', 'mental health platform', 'therapy booking', 'find therapist process']
        }}
        enabledFeatures={{
          multilingual: true,
          faqSchema: true,
          trustSignals: true
        }}
      />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-brand-blue-50 to-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-brand-gray-900 mb-6">
              {t('howItWorks')}
            </h1>
            <p className="text-xl text-brand-gray-600 mb-8 leading-relaxed">
              {t('discoverSimpleWayToAccessTherapy')}
            </p>
            <Badge variant="secondary" className="text-sm px-4 py-2">
              {t('certifiedByDrJoseLuisMarin')}
            </Badge>
          </div>
        </div>
      </div>

      {/* Process Steps */}
      <div className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-brand-gray-900 mb-12">
              {t('fourSimpleSteps')}
            </h2>
            
            <div className="space-y-12">
              {steps.map((step, index) => (
                <div key={step.number} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-12`}>
                  <div className="flex-1">
                    <Card className="h-full shadow-lg hover:shadow-xl transition-shadow">
                      <CardHeader>
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 bg-brand-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                            {step.number}
                          </div>
                          <step.icon className="w-8 h-8 text-brand-blue-500" />
                        </div>
                        <CardTitle className="text-2xl text-brand-gray-900">
                          {step.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-brand-gray-600 mb-6 leading-relaxed">
                          {step.description}
                        </p>
                        <ul className="space-y-2">
                          {step.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <span className="text-brand-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="flex-1">
                    <div className="bg-brand-gray-100 rounded-xl h-64 flex items-center justify-center">
                      <step.icon className="w-24 h-24 text-brand-gray-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-brand-gray-50 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-brand-gray-900 mb-12">
              {t('whyChooseOurPlatform')}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-8">
                    <benefit.icon className="w-12 h-12 text-brand-blue-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-brand-gray-900 mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-brand-gray-600 text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-gray-900 mb-6">
              {t('readyToStart')}
            </h2>
            <p className="text-xl text-brand-gray-600 mb-8">
              {t('joinThousandsWhoFoundHelp')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={getLocalizedPath('/find')}>
                <Button size="lg" className="w-full sm:w-auto">
                  {t('findTherapist')}
                </Button>
              </Link>
              <Link to={getLocalizedPath('/for-therapists')}>
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  {t('joinAsTherapist')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HowItWorks;
